<template>
    <el-dialog :title="title"
               :visible="showDialog"
               append-to-body
               custom-class="variations-dialog"
               :width="dialogWidth"
               @close="close"
               @open="open">
               <!-- título y selector de vista en la misma línea del encabezado -->
        <div slot="title"
             class="variations-header">
            <span class="el-dialog__title">Variaciones · {{ title }}</span>
            <el-radio-group v-model="viewMode"
                            :disabled="loading"
                            size="small">
                <el-radio-button label="attributes">Atributos</el-radio-button>
                <el-radio-button label="list">Listado</el-radio-button>
            </el-radio-group>
        </div>
        <div v-loading="loading">
            <!-- vista Atributos: foto, botones de cada atributo y resumen de la variante elegida -->
            <div v-show="viewMode === 'attributes'"
                 class="row">
                <div class="col-md-4 mb-1 text-center">
                    <img v-if="imageUrl"
                         :alt="imageAlt"
                         :src="imageUrl"
                         class="img-thumbnail"
                         style="max-height: 250px; object-fit: contain;">
                </div>
                <div class="col-md-8">
                    <template v-if="selectedVariation">
                        <h3 style="font-weight: 700;">{{ selectedVariation.description }}</h3>
                        <h3 style="font-weight: 700;">{{ selectedVariation.currency_type_symbol }} {{ selectedVariation.sale_unit_price }}</h3>
                    </template>
                    <p v-else-if="!loading"
                       class="text-muted">Esta combinación no está disponible</p>
                    <div v-for="attribute in attributes"
                         :key="attribute.id"
                         class="mb-3">
                        <label class="attribute-label d-block">{{ attribute.name }}</label>
                        <el-radio-group v-model="selected[attribute.id]"
                                        class="attribute-options"
                                        size="small">
                            <el-radio-button v-for="value in attribute.values"
                                             :key="value.id"
                                             :class="{'has-stock': valueHasStock(attribute, value)}"
                                             :label="value.id">
                                <span v-if="value.color" :style="colorDotStyle(value.color)"></span>{{ value.value }}
                            </el-radio-button>
                        </el-radio-group>
                    </div>
                    <p v-if="selectedVariation" class="mb-0">
                        <span :class="{'text-danger': selectedWithoutStock}">Disponible: {{ Number(selectedVariation.stock) }}</span>
                        <span v-if="selectedWithoutStock"
                                class="badge badge-danger ml-1">Sin stock</span>
                        <span v-else
                              class="badge badge-success ml-1">En Stock</span>
                        <br>
                    </p>
                    <p v-if="selectedVariation && (selectedVariation.internal_id || selectedVariation.barcode)"
                       class="text-muted mb-0">
                        <template v-if="selectedVariation.internal_id">Cód. interno: {{ selectedVariation.internal_id }}</template>
                        <template v-if="selectedVariation.internal_id && selectedVariation.barcode"> · </template>
                        <template v-if="selectedVariation.barcode">Cód. de barras: {{ selectedVariation.barcode }}</template>
                    </p>
                </div>
            </div>

            <!-- vista Listado: buscador y variantes agrupadas por color, con su stock -->
            <el-input v-show="viewMode === 'list'"
                      ref="searchInput"
                      v-model="search"
                      class="mb-3"
                      clearable
                      placeholder="Buscar por atributos, Cód. interno o Cód. de barras"
                      prefix-icon="el-icon-search"
                      size="small"></el-input>

            <p v-show="viewMode === 'list' && filteredVariations.length === 0"
               class="text-muted text-center mb-0">
                No hay variantes que coincidan con la búsqueda
            </p>

            <table v-show="viewMode === 'list' && filteredVariations.length > 0"
                   class="table table-hover mb-0 variations-table">
                <thead>
                <tr>
                    <th class="">Variación</th>
                   <th class="text-right">Stock</th>
                    <th class="text-right">Precio</th>
                </tr>
                </thead>
                <tbody>
                <template v-for="group in variationGroups">
                    <tr v-if="group.value"
                        :key="'group-' + group.id"
                        class="variations-group">
                        <td colspan="3">
                            <span :style="colorDotStyle(group.color)"></span><strong>{{ group.value }}</strong>
                        </td>
                    </tr>
                    <tr v-for="variation in group.variations"
                        :key="variation.id"
                        :class="{'table-active': selectedVariation && selectedVariation.id === variation.id}"
                        style="cursor: pointer"
                        @click="selectVariation(variation)">
                        <td :class="{'pl-5': group.value}"
                            class=""
                            :title="variation.description">
                            {{ variationShortName(variation) }}
                            <small v-if="variation.internal_id || variation.barcode"
                                   class="d-block text-muted">
                                <template v-if="variation.internal_id">Cód. interno: {{ variation.internal_id }}</template>
                                <!--  <template v-if="variation.internal_id && variation.barcode"> · </template>  --> <br>
                                <template v-if="variation.barcode">Cód. barras: {{ variation.barcode }}</template>
                            </small>
                        </td>
                        <td :class="{'text-danger': Number(variation.stock) <= 0}"
                            class="text-right">{{ Number(variation.stock) }}
                        </td>
                        <td class="text-right">{{ variation.currency_type_symbol }} {{ variation.sale_unit_price }}</td>
                    </tr>
                </template>
                </tbody>
            </table>
        </div>
        <span slot="footer">
            <el-button @click="close">Cancelar</el-button>
            <el-button :disabled="!selectedVariation"
                       type="primary"
                       @click="add">Agregar</el-button>
        </span>
    </el-dialog>
</template>

<script>
import {colorDotStyle} from '@helpers/attribute_colors'

export default {
    props: ['showDialog', 'parent'],
    data() {
        return {
            loading: false,
            attributes: [],
            variations: [],
            selected: {},
            viewMode: 'attributes',
            search: '',
        }
    },
    computed: {
        title() {
            return this.parent ? this.parent.description : 'Variaciones'
        },
        dialogWidth() {
            // el listado solo tiene tres columnas cortas: el modal se achica
            return this.viewMode === 'list' ? '475px' : '760px'
            //return this.viewMode === 'list' ? 'fit-content' : '760px'
        },
        imageUrl() {
            // por defecto la foto del producto principal; al elegir una variación con foto propia, la suya
            const variation = this.selectedVariation
            if (variation && variation.image_url && !variation.image_url.includes('imagen-no-disponible')) {
                return variation.image_url
            }
            return this.parent ? this.parent.image_url : null
        },
        imageAlt() {
            if (this.selectedVariation) return this.selectedVariation.description
            return this.parent ? this.parent.description : ''
        },
        selectedVariation() {
            // la variación cuyos valores coinciden con todos los atributos elegidos
            if (this.attributes.length === 0) return null
            if (this.attributes.some(attribute => !this.selected[attribute.id])) return null

            return this.variations.find(variation => this.attributes.every(attribute =>
                variation.variation_values[attribute.id] === this.selected[attribute.id]
            )) || null
        },
        selectedWithoutStock() {
            // la variante elegida no tiene stock (0 o negativo)
            return this.selectedVariation !== null && Number(this.selectedVariation.stock) <= 0
        },
        colorAttribute() {
            // el atributo cuyos valores tienen color, por ejemplo COLOR
            return this.attributes.find(attribute => attribute.values.some(value => value.color)) || null
        },
        sortedVariations() {
            // las variantes en el orden de los valores de cada atributo
            const position = (attribute, variation) =>
                attribute.values.findIndex(value => value.id === variation.variation_values[attribute.id])

            return this.variations.slice().sort((a, b) => {
                for (let i = 0; i < this.attributes.length; i++) {
                    const difference = position(this.attributes[i], a) - position(this.attributes[i], b)
                    if (difference !== 0) return difference
                }
                return 0
            })
        },
        valueNames() {
            // nombres de todos los valores de los atributos, normalizados: ["rojo", "negro", "m", "l", "xl"]
            return this.attributes.reduce((names, attribute) =>
                names.concat(attribute.values.map(value => this.normalizeText(value.value))), [])
        },
        filteredVariations() {
            // búsqueda en pantalla: todas las palabras escritas deben coincidir con la variante
            const words = this.normalizeText(this.search).split(' ').filter(word => word)
            if (words.length === 0) return this.sortedVariations

            return this.sortedVariations.filter(variation => {
                const names = this.variationValueNames(variation).map(name => this.normalizeText(name))
                                const text = this.normalizeText([variation.description, variation.internal_id, variation.barcode].concat(names).join(' '))

                return words.every(word => this.valueNames.includes(word)
                    // la palabra es el nombre exacto de un valor, por ejemplo "L": solo coincide con ese valor
                    ? names.includes(word)
                    // si no, se busca dentro del nombre o del código, por ejemplo "golden" o "neg"
                    : text.includes(word))
            })
        },
        variationGroups() {
            // con un atributo de color, el listado se agrupa por color; si no, va en un solo grupo
            if (!this.colorAttribute) {
                return [{id: 'all', value: null, color: null, variations: this.filteredVariations}]
            }

            return this.colorAttribute.values
                .map(value => ({
                    id: value.id,
                    value: value.value,
                    color: value.color,
                    variations: this.filteredVariations.filter(variation =>
                        variation.variation_values[this.colorAttribute.id] === value.id
                    ),
                }))
                .filter(group => group.variations.length > 0)
        },
    },
    watch: {
        viewMode(mode) {
            // al abrir el listado, el cursor queda en el buscador
            if (mode === 'list') this.$nextTick(() => this.$refs.searchInput.focus())
        },
    },
    methods: {
        colorDotStyle,
        normalizeText(text) {
            // minúsculas y sin tildes, para que "rojo" encuentre "ROJO" y "marron" encuentre "MARRÓN"
            return String(text || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim()
        },
        variationValueNames(variation) {
            // nombres de los valores de la variante, por ejemplo ["NEGRO", "L"]
            return this.attributes.map(attribute => {
                const value = attribute.values.find(item => item.id === variation.variation_values[attribute.id])
                return value ? value.value : ''
            })
        },
        valueHasStock(attribute, value) {
            // el valor tiene stock si alguna variante con ese valor y con los demás atributos ya marcados tiene stock
            return this.variations.some(variation =>
                Number(variation.stock) > 0 &&
                this.attributes.every(item => {
                    const valueId = item.id === attribute.id ? value.id : this.selected[item.id]
                    return !valueId || variation.variation_values[item.id] === valueId
                })
            )
        },
        async open() {
            this.loading = true
            this.attributes = []
            this.variations = []
            this.selected = {}
            this.viewMode = 'attributes'
            this.search = ''
            try {
                const response = await this.$http.get(`/pos/variations/${this.parent.item_id}`)
                const selected = {}
                response.data.attributes.forEach(attribute => {
                    selected[attribute.id] = null
                })
                this.selected = selected
                this.attributes = response.data.attributes
                this.variations = response.data.variations

                // por defecto queda marcada la primera variante con stock (o la primera, si ninguna tiene)
                const variation = this.sortedVariations.find(row => Number(row.stock) > 0) || this.sortedVariations[0]
                if (variation) this.selectVariation(variation)
            } finally {
                this.loading = false
            }
        },
        variationShortName(variation) {
            // solo los valores de la variante, por ejemplo "ROJO / M"; el nombre completo queda al pasar el mouse
            const names = this.variationValueNames(variation).filter(name => name)
            return names.length ? names.join(' / ') : variation.description
        },
        selectVariation(variation) {
            // al hacer clic en una fila se marcan sus atributos
            const selected = {}
            this.attributes.forEach(attribute => {
                selected[attribute.id] = variation.variation_values[attribute.id]
            })
            this.selected = selected
        },
        add() {
            this.$emit('add', this.selectedVariation)
            this.close()
        },
        close() {
            this.$emit('update:showDialog', false)
        },
    },
}
</script>
<style scoped>
/* título a la izquierda y selector de vista a la derecha, en una sola línea */
.variations-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}


/* un nombre de producto largo se corta con "..." en lugar de empujar al selector */
.variations-header .el-dialog__title {
    /* ancho 0 + crecer: el título no ensancha el modal, solo usa el espacio que deja el selector */
    flex-grow: 1;
    width: 0;
    min-width: 0;
    margin-right: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.variations-header .el-radio-group {
    flex-shrink: 0;
}
/* más espacio alrededor del nombre de cada variación */
.variations-table th,
.variations-table td {
    padding: 0.6rem 1rem;
    vertical-align: middle;
}


/* fila con el nombre de cada color en el listado */
.variations-table .variations-group td {
    background-color: #f5f7fa;
}
</style>
<style>
/* el POS quita el espacio del encabezado de todos los modales; aquí se recupera solo para este */
.variations-dialog .el-dialog__header {
    padding: 20px 50px 15px 20px !important;
    border-bottom: 1px solid #ebeef5;
}
/* la X de cerrar, centrada con el selector de vista */
.variations-dialog .el-dialog__headerbtn {
    top: 28px;
}
/* menos espacio arriba del contenido; Element trae 30px */
.variations-dialog .el-dialog__body {
    padding: 12px 20px;
}
/* en el Listado el modal se ajusta a la tabla; el mínimo evita que salte de ancho al filtrar */
.variations-dialog {
    min-width: 420px;
}

/* nombre de cada atributo: gris, pequeño y en mayúsculas */
.variations-dialog .attribute-label {
    margin-bottom: 8px;
    color: #909399;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
}

/* botones de valores separados entre sí, en lugar de pegados como un grupo */
.variations-dialog .attribute-options .el-radio-button {
    margin: 0 8px 8px 0;
    border-radius: 20px; /* el brillo azul del foco con teclado también queda redondo */
}

/* cada valor es una píldora con borde punteado; con una o dos letras queda como círculo */
.variations-dialog .attribute-options .el-radio-button .el-radio-button__inner {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    height: 40px;
    padding: 0 14px;
    border: 1px dashed #dcdfe6;
    border-radius: 20px;
    background-color: #fff;
    box-shadow: none;
    color: #c0c4cc;
    font-size: 13px;
    text-transform: uppercase;
}

.variations-dialog .attribute-options .el-radio-button .el-radio-button__inner:hover {
    color: #606266;
}

/* valor elegido: borde punteado oscuro y más grueso, sin relleno */
.variations-dialog .attribute-options .el-radio-button.is-active .el-radio-button__inner {
    padding: 0 13px; /* 1px menos por lado: compensa el borde de 2px y el botón no cambia de ancho */
    border: 2px dashed #303133;
    border-color: #303133 !important;
    background-color: #fff !important;
    box-shadow: none !important;
    color: #303133;
}

/* valor con stock: borde sólido en lugar de punteado (elegido o no);
   va después de la regla .is-active para ganarle en el estilo del borde */
.variations-dialog .attribute-options .el-radio-button.has-stock .el-radio-button__inner {
    border-style: solid;
}

</style>



// WEBPACK FOOTER //
// resources/js/views/tenant/pos/partials/variations.vue